import { css } from 'styled-components';
import { rgba } from 'polished';
import { SDS_MOTION_DURATION_FASTEST, SDS_MOTION_EASING_LINEAR } from '@s-group/design-system-tokens/web/tokens/motion.es6.js';
import { SDS_SIZE_BORDER_LARGE, SDS_SIZE_BORDER_RADIUS_XSMALL, SDS_SIZE_BORDER_RADIUS_NONE } from '@s-group/design-system-tokens/web/tokens/size.es6.js';
import { SDS_SHADOW_ELEVATION_LOW_Y, SDS_SHADOW_ELEVATION_LOW_BLUR, SDS_SHADOW_ELEVATION_HIGH_Y, SDS_SHADOW_ELEVATION_HIGH_BLUR, SDS_SHADOW_ELEVATION_MIDDLE_Y, SDS_SHADOW_ELEVATION_MIDDLE_BLUR } from '@s-group/design-system-tokens/web/tokens/shadow.es6.js';
import { SDS_COLOR_BLACK } from '@s-group/design-system-tokens/web/tokens/colors.es6.js';
import { SDS_MISC_ALPHA_XXSMALL, SDS_MISC_ALPHA_XSMALL } from '@s-group/design-system-tokens/web/tokens/misc.es6.js';
import { SDS_SPACE_XXXXSMALL } from '@s-group/design-system-tokens/web/tokens/space.es6.js';
import { SDS } from '../common/namespace.js';

const borderStyle = ({ variant, theme, }) => {
    if (variant === 'outlined') {
        return `${SDS_SIZE_BORDER_LARGE} solid ${theme[SDS].colors.SDS_BRAND_COLOR_BORDER_WEAK_NEUTRAL}`;
    }
    else {
        return 'transparent';
    }
};
const backgroundColor = ({ variant, theme, }) => {
    if (variant === 'elevated')
        return `${theme[SDS].colors.SDS_BRAND_COLOR_ELEVATION_LOW}`;
    if (variant === 'plain')
        return `transparent`;
    return `${theme[SDS].colors.SDS_BRAND_COLOR_ELEVATION_FLAT}`;
};
const hoverBackground = ({ hasLink, variant, theme, }) => {
    if (!hasLink)
        return;
    if (variant === 'plain') {
        return `${rgba(theme[SDS].colors.SDS_BRAND_COLOR_BACKGROUND_INVERSE_NEUTRAL, SDS_MISC_ALPHA_XXSMALL)}`;
    }
    return `${theme[SDS].colors.SDS_BRAND_COLOR_ELEVATION_HIGH}`;
};
const activeBackground = ({ hasLink, variant, theme, }) => {
    if (!hasLink)
        return;
    if (variant === 'plain') {
        return `${rgba(theme[SDS].colors.SDS_BRAND_COLOR_BACKGROUND_INVERSE_NEUTRAL, SDS_MISC_ALPHA_XSMALL)}`;
    }
    return `${theme[SDS].colors.SDS_BRAND_COLOR_ELEVATION_MIDDLE}`;
};
const boxShadow = ({ linkFocusState, theme, variant, }, state = 'default') => {
    const shadowColor = `${rgba(SDS_COLOR_BLACK, 0.16)}`;
    const shadows = {
        default: 
        // Used only with elevated variant
        `0 ${SDS_SHADOW_ELEVATION_LOW_Y} ${SDS_SHADOW_ELEVATION_LOW_BLUR} 0 ${shadowColor}`,
        hover: `0 ${SDS_SHADOW_ELEVATION_HIGH_Y} ${SDS_SHADOW_ELEVATION_HIGH_BLUR} 0 ${shadowColor}`,
        active: `0 ${SDS_SHADOW_ELEVATION_MIDDLE_Y} ${SDS_SHADOW_ELEVATION_MIDDLE_BLUR} 0 ${shadowColor}`,
        focus: 
        // Fake inner border used on block link focus
        `0 0 0 ${SDS_SIZE_BORDER_LARGE} ${theme[SDS].colors.SDS_BRAND_COLOR_BORDER_INVERSE_NEUTRAL}`,
    };
    const hasDualShadow = variant !== 'plain' && (state !== 'default' || variant === 'elevated');
    if (linkFocusState) {
        if (hasDualShadow)
            return [shadows['focus'], shadows[state]].join(', ');
        return shadows['focus'];
    }
    else if (variant === 'elevated' || state !== 'default') {
        return shadows[state];
    }
};
const borderRadius = (props) => {
    const { rounding } = props;
    if (rounding === 'rounded')
        return `${SDS_SIZE_BORDER_RADIUS_XSMALL}`;
    if (rounding === 'sharp')
        return `${SDS_SIZE_BORDER_RADIUS_NONE}`;
    return '';
};
const CardStyle = css `
    background-color: ${backgroundColor};
    border: ${borderStyle};
    border-radius: ${borderRadius};
    box-shadow: ${boxShadow};
    box-sizing: border-box;
    display: block;
    position: relative;
    transition: box-shadow ${SDS_MOTION_DURATION_FASTEST}
        cubic-bezier(${SDS_MOTION_EASING_LINEAR});

    &:hover {
        background-color: ${hoverBackground};
        box-shadow: ${props => boxShadow(props, props.hasLink ? 'hover' : 'default')};
    }

    &:active {
        background-color: ${activeBackground};
        box-shadow: ${props => boxShadow(props, props.hasLink ? 'active' : 'default')};
    }

    &:focus-within {
        outline: ${({ linkFocusState, theme }) => linkFocusState &&
    `${SDS_SIZE_BORDER_LARGE} solid
            ${theme[SDS].colors.SDS_BRAND_COLOR_BORDER_FOCUS}`};
        outline-offset: ${SDS_SPACE_XXXXSMALL};
    }
`;

export { CardStyle };
//# sourceMappingURL=Card.styles.js.map
